import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError, formatBytes } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatusBadge from "@/components/StatusBadge";
import { Cloud, HardDrive, Users, Plus, Trash2, Loader2, Link2 } from "lucide-react";
import { toast } from "sonner";
import { FaGoogle, FaMicrosoft } from "react-icons/fa";

export default function CloudMigration() {
  const [params, setParams] = useSearchParams();
  const [config, setConfig] = useState({});
  const [accounts, setAccounts] = useState([]);
  const [migs, setMigs] = useState([]);
  const [form, setForm] = useState({ name: "", kind: "files", source_account_id: "", dest_account_id: "" });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    const [c, a, m] = await Promise.all([
      api.get("/cloud/config"), api.get("/cloud/accounts"), api.get("/cloud/migrations"),
    ]);
    setConfig(c.data); setAccounts(a.data); setMigs(m.data);
  };

  useEffect(() => {
    load();
    if (params.get("connected")) { toast.success(`${params.get("connected")} account connected`); setParams({}); }
    if (params.get("error")) { toast.error(`Connect failed: ${params.get("error")}`); setParams({}); }
    const t = setInterval(() => api.get("/cloud/migrations").then((r) => setMigs(r.data)).catch(() => {}), 3000);
    return () => clearInterval(t);
  }, []);

  const connect = async (provider) => {
    try {
      const { data } = await api.get(`/oauth/${provider}/authorize`);
      window.location.href = data.url;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  const removeAcc = async (id) => {
    await api.delete(`/cloud/accounts/${id}`); toast.success("Disconnected"); load();
  };

  const create = async () => {
    if (!form.name || !form.source_account_id || !form.dest_account_id) { toast.error("Fill name, source & destination"); return; }
    if (form.source_account_id === form.dest_account_id) { toast.error("Source and destination must differ"); return; }
    setCreating(true);
    try {
      await api.post("/cloud/migrations", form);
      toast.success("Cloud migration started");
      setForm({ name: "", kind: "files", source_account_id: "", dest_account_id: "" });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setCreating(false); }
  };

  const accLabel = (a) => `${a.provider === "google" ? "Google" : "Microsoft"} · ${a.email}`;

  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Cloud</p>
        <h1 className="mt-1 font-heading text-4xl font-bold tracking-tighter">Cloud Migration</h1>
        <p className="mt-2 text-muted-foreground">Move files (OneDrive ↔ Google Drive) and contacts between accounts.</p>
      </div>

      {/* Connect */}
      <div className="mb-6 border border-border bg-card p-6">
        <h2 className="mb-4 flex items-center gap-2 font-semibold tracking-tight"><Link2 className="h-4 w-4" /> Connected accounts</h2>
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" className="gap-2" disabled={!config.google} onClick={() => connect("google")} data-testid="connect-google-button">
            <FaGoogle /> Connect Google
          </Button>
          <Button variant="outline" className="gap-2" disabled={!config.microsoft} onClick={() => connect("microsoft")} data-testid="connect-microsoft-button">
            <FaMicrosoft /> Connect Microsoft
          </Button>
        </div>
        <div className="mt-4 space-y-2">
          {accounts.length === 0 ? <p className="text-sm text-muted-foreground">No accounts connected yet.</p> :
            accounts.map((a) => (
              <div key={a.id} className="flex items-center justify-between border border-border px-4 py-2" data-testid={`account-${a.id}`}>
                <span className="flex items-center gap-2 text-sm">{a.provider === "google" ? <FaGoogle /> : <FaMicrosoft />} <span className="font-mono">{a.email}</span></span>
                <Button variant="ghost" size="icon" onClick={() => removeAcc(a.id)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
              </div>
            ))}
        </div>
      </div>

      {/* Create */}
      <div className="mb-8 border border-border bg-card p-6">
        <h2 className="mb-4 font-semibold tracking-tight">New cloud migration</h2>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1"><Label className="text-xs uppercase tracking-[0.15em]">Name</Label>
            <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="cloud-name-input" /></div>
          <div className="space-y-1"><Label className="text-xs uppercase tracking-[0.15em]">What to migrate</Label>
            <Select value={form.kind} onValueChange={(v) => setForm((f) => ({ ...f, kind: v }))}>
              <SelectTrigger data-testid="cloud-kind-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="files">Files (Drive/OneDrive)</SelectItem><SelectItem value="contacts">Contacts</SelectItem></SelectContent>
            </Select></div>
          <div className="space-y-1"><Label className="text-xs uppercase tracking-[0.15em]">Source account</Label>
            <Select value={form.source_account_id} onValueChange={(v) => setForm((f) => ({ ...f, source_account_id: v }))}>
              <SelectTrigger data-testid="cloud-source-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{accLabel(a)}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1"><Label className="text-xs uppercase tracking-[0.15em]">Destination account</Label>
            <Select value={form.dest_account_id} onValueChange={(v) => setForm((f) => ({ ...f, dest_account_id: v }))}>
              <SelectTrigger data-testid="cloud-dest-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{accLabel(a)}</SelectItem>)}</SelectContent>
            </Select></div>
        </div>
        <Button className="mt-5 gap-2" onClick={create} disabled={creating} data-testid="cloud-create-button">
          {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Start migration
        </Button>
      </div>

      {/* List */}
      <h2 className="mb-4 text-lg font-semibold tracking-tight">Cloud migrations</h2>
      {migs.length === 0 ? (
        <div className="flex flex-col items-center border border-dashed border-border bg-card py-16 text-center">
          <Cloud className="h-10 w-10 text-muted-foreground" /><p className="mt-3 text-sm text-muted-foreground">No cloud migrations yet.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {migs.map((m) => {
            const pct = m.total > 0 ? Math.round((m.migrated / m.total) * 100) : 0;
            return (
              <div key={m.id} className="border border-border bg-card p-6" data-testid={`cloud-mig-${m.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    {m.kind === "contacts" ? <Users className="h-4 w-4 text-primary" /> : <HardDrive className="h-4 w-4 text-primary" />}
                    <span className="font-semibold tracking-tight">{m.name}</span><StatusBadge status={m.status} />
                  </div>
                  <span className="font-mono text-xs text-muted-foreground">{m.migrated}/{m.total || "?"} · {formatBytes(m.bytes_transferred)}</span>
                </div>
                <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-muted">
                  <div className={`h-full rounded-full bg-primary ${m.status === "running" ? "progress-active" : ""}`} style={{ width: `${pct}%` }} />
                </div>
                {m.logs && <pre className="mt-3 max-h-32 overflow-y-auto rounded bg-slate-950 p-3 font-mono text-[11px] text-slate-300">{m.logs}</pre>}
                {m.error && <p className="mt-2 text-xs text-red-600">{m.error}</p>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
